/* begin general import */

import Add16 from "@carbon/icons-react/es/add/16";
import TrashCan16 from "@carbon/icons-react/es/trash-can/16";
import { Col, Popconfirm, Row } from "antd";
import Table, { ColumnProps } from "antd/lib/table";
import { Role, RoleFilter } from "models/Role";
import React from "react";
import {
  Button,
  FormItem,
  LayoutCell,
  Modal,
  OneLineText,
  Select,
} from "react3l-ui-library";
import nameof from "ts-nameof.macro";
import { Close20 } from "@carbon/icons-react";
import { ResponsibilityRoleMapping } from "models/ResponsibilityRoleMapping";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import { responsibilityRepository } from "repositories/responsibility-repository";

interface ResponsibilityRoleModalProps {
  siteId: number;
  isTendering?: boolean;
  listResponsibilityRole?: ResponsibilityRoleMapping[];
  visibleResponsibilityRoleModal?: boolean;
  handleCloseResponsibilityRoleModal?: () => void;
  handleSaveResponsibilityRoleModal?: (selectedList: Role[]) => void;
}

export function ResponsibilityRoleModal(props: ResponsibilityRoleModalProps) {
  const {
    visibleResponsibilityRoleModal,
    listResponsibilityRole,
    siteId,
    isTendering,
    handleCloseResponsibilityRoleModal,
    handleSaveResponsibilityRoleModal,
  } = props;

  const [translate] = useTranslation();

  /* inject the current record's isTendering into the single-list-role payload */
  const getListRole = React.useCallback(
    (filter: RoleFilter) =>
      responsibilityRepository.singleListRole({
        ...filter,
        isTendering,
      } as any),
    [isTendering]
  );

  const [responsibilityRole, setResponsibilityRole] =
    React.useState<ResponsibilityRoleMapping>(undefined);

  const [selectedList, setSelectedList] = React.useState<
    ResponsibilityRoleMapping[]
  >([]);

  const [roleFilter, setRoleFilter] = React.useState<RoleFilter>({
    ...new RoleFilter(),
    siteId: { equal: siteId },
  });
  const ref = React.useRef<boolean>(true);

  React.useEffect(() => {
    if (visibleResponsibilityRoleModal) {
      if (ref.current) {
        if (listResponsibilityRole && listResponsibilityRole.length > 0) {
          const cloneListResponsibilityRole = _.cloneDeep(
            listResponsibilityRole
          );
          setSelectedList([...cloneListResponsibilityRole]);
          const ids = cloneListResponsibilityRole.map(
            (responsibilityRole) => responsibilityRole?.roleId
          );
          roleFilter.id.notIn = ids;
          setRoleFilter({ ...roleFilter });
          ref.current = false;
        } else {
          setSelectedList([]);
          ref.current = false;
        }
      }
    } else {
      ref.current = true;
    }
  }, [listResponsibilityRole, roleFilter, visibleResponsibilityRoleModal]);

  const hanldeChangeMappingRole = React.useCallback((valueId, valueObject) => {
    const responsibilityRoleObj = {
      role: valueObject,
      roleId: valueId,
      key: uuidv4(),
    };
    setResponsibilityRole(responsibilityRoleObj);
  }, []);

  const handleAddRole = React.useCallback(() => {
    if (responsibilityRole?.roleId !== undefined) {
      const ids = selectedList.map(
        (responsibilityRole) => responsibilityRole.roleId
      );
      if (!ids.includes(responsibilityRole?.roleId)) {
        selectedList.push(responsibilityRole);
        setSelectedList([...selectedList]);
        ids.push(responsibilityRole.roleId);
      }
      roleFilter.id.notIn = ids;
      setRoleFilter({ ...roleFilter });
      setResponsibilityRole(undefined);
    }
  }, [responsibilityRole, roleFilter, selectedList]);

  const handleDeleteItem = React.useCallback(
    (index: number) => {
      if (index > -1) {
        selectedList.splice(index, 1);
      }
      setSelectedList([...selectedList]);
      setResponsibilityRole(undefined);
    },
    [selectedList]
  );

  const handleSave = React.useCallback(() => {
    if (typeof handleSaveResponsibilityRoleModal === "function") {
      setResponsibilityRole(undefined);
      handleSaveResponsibilityRoleModal(selectedList);
    }
  }, [handleSaveResponsibilityRoleModal, selectedList]);

  const columns: ColumnProps<Role>[] = React.useMemo(() => {
    return [
      {
        title: (
          <div className="text-left gradient-text">
            {translate("responsibilities.roles.code")}
          </div>
        ),
        key: nameof(selectedList[0].code),
        dataIndex: nameof(selectedList[0].role),
        width: 150,
        ellipsis: true,
        render(...params: [Role, ResponsibilityRoleMapping, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText countCharacters={50} value={params[0]?.code} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <div className="text-left gradient-text">
            {translate("responsibilities.roles.name")}
          </div>
        ),
        width: 150,

        key: nameof(selectedList[0].name),
        dataIndex: nameof(selectedList[0].role),
        ellipsis: true,
        render(...params: [Role, ResponsibilityRoleMapping, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText countCharacters={50} value={params[0]?.name} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <div className="text-center gradient-text">
            {translate("general.actions.label")}
          </div>
        ),
        key: "action",
        dataIndex: nameof(selectedList[0].id),
        fixed: "right",
        width: 80,
        align: "center",
        render(...[, , index]) {
          return (
            <div className="d-flex justify-content-center button-action-table">
              <Popconfirm
                placement="left"
                title={translate("general.deletes.content")}
                onConfirm={() => handleDeleteItem(index)}
                okText={translate("general.actions.delete")}
                cancelText={translate("general.actions.cancel")}
              >
                <button className="btn btn-sm ">
                  <TrashCan16 color="red" />
                </button>
              </Popconfirm>
            </div>
          );
        },
      },
    ];
  }, [handleDeleteItem, selectedList, translate]);

  const handleClose = React.useCallback(() => {
    setRoleFilter(new RoleFilter());
    handleCloseResponsibilityRoleModal();
  }, [handleCloseResponsibilityRoleModal]);

  return (
    <div>
      <Modal
        size={1024}
        visible={visibleResponsibilityRoleModal}
        visibleFooter={true}
        handleSave={handleSave}
        handleCancel={handleClose}
        titleButtonCancel={translate("general.actions.close")}
        titleButtonApply={translate("general.actions.save")}
        className="m-t--sm"
        title={translate("responsibilities.roles.assignRole")}
        closable={false}
        wrapClassName={"dxg-modal-wrap"}
      >
        <div className="modal-content">
          <Row className="d-flex justify-content-between align-items-center m-t--xs">
            <Col lg={14}>
              <FormItem>
                <Select
                  type={0}
                  classFilter={RoleFilter}
                  searchProperty={"name"}
                  placeHolder={translate(
                    "responsibilities.roles.placeholder.role"
                  )}
                  getList={getListRole}
                  onChange={hanldeChangeMappingRole}
                  value={responsibilityRole?.role}
                  valueFilter={roleFilter}
                />
              </FormItem>
            </Col>
            <Col>
              <Button
                type="primary"
                className="btn--lg btn-orange-export"
                icon={<Add16 />}
                onClick={handleAddRole}
              >
                {translate("general.actions.addRole")}
              </Button>
            </Col>
          </Row>
          <Row>
            <Table
              tableLayout="fixed"
              columns={columns}
              dataSource={selectedList}
              pagination={false}
              rowKey={nameof(selectedList[0].key)}
              className=" m-t--xs"
              scroll={{ y: 300 }}
            />
          </Row>
        </div>

        <span className={"close-modal"} onClick={handleClose}>
          <Close20 />
        </span>
      </Modal>
    </div>
  );
}

export default ResponsibilityRoleModal;
